/* global HSCore */

import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import Footer from "../../Footer";
import LanguageContext from "../../../utils/Contexts/LanguageContext";
import _ from "lodash";
import UserContext from "../../../utils/Contexts/UserContext";
import toast from "react-hot-toast";
import ImportDetailsTable from "./DetailsTable";
import axiosInstance from "../../../utils/Requests/axios";
import { useLocation } from "react-router-dom";
import Constant from "../../Constants";
import ImportFilterModal from "../../../modals/ImportFilterModal";
import FilterIcon from "../../svg/FilterIcon";
import TablePagination from "../../Common/Pagination";

const ImportDetails = () => {
  const location = useLocation();
  const selectedItem = location.state?.item;

  const { t } = useContext(LanguageContext);
  const { importStatus } = Constant();
  const { userData } = useContext(UserContext);

  const [pendingFilters, setPendingFilters] = useState([]);
  const [activeParsTab, setActiveParsTab] = useState(null);
  const [activeProcessTab, setActiveProcessTab] = useState(null);
  const [pageCount, setPageCount] = useState(10);
  const [activePage, setActivePage] = useState(1);
  const [columns, setColumns] = useState({
    input: true,
    parsing: true,
    processing: true,
    date: true,
  });

  const allParsing = [
    { value: null, name: t("All Parsing") },
    { value: 1, name: t("Successful") },
    { value: 2, name: t("Failed") },
  ];

  const allProcessing = [
    { value: null, name: t("All Processing") },
    { value: 1, name: t("Successful") },
    { value: 2, name: t("Failed") },
    { value: 0, name: t("Not Started") },
  ];

  const updatePendingFilter = (key, value) => {
    setPendingFilters((prevFilters) => {
      const updatedFilters = prevFilters.map((filter) =>
        filter.key === key ? { key, value } : filter
      );

      const finalFilters = updatedFilters.some((filter) => filter.key === key)
        ? updatedFilters
        : [...updatedFilters, { key, value }];

      return finalFilters;
    });
  };

  const handleParsingTabClick = (relValue) => {
    setActiveParsTab(relValue);
    updateFilter("PARSING", relValue);
  };

  const handleProcessingTabClick = (relValue) => {
    setActiveProcessTab(relValue);
    updateFilter("PROCESSING", relValue);
  };

  const handleToggle = (column) => {
    setColumns((prevColumns) => ({
      ...prevColumns,
      [column]: !prevColumns[column],
    }));
  };

  const [columnSorts, setColumnSorts] = useState({
    0: null,
    1: null,
    2: null,
    3: null,
  });
  const [tableData, setTableData] = useState(null);
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [formData, setFormData] = useState({
    draw: 1,
    columns: [
      { data: "input" },
      { data: "parsing" },
      { data: "processing" },
      { data: "created" },
    ],
    order: [],
    start: 0,
    length: 10,
    search: {
      value: "",
      filters: [],
    },
  });

  useEffect(() => {
    const payload = { ...formData };
    const start = activePage === 1 ? 0 : (activePage - 1) * pageCount;
    if (payload.start !== start || payload.length !== pageCount) {
      payload.start = start;
      payload.length = pageCount;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, pageCount]);

  const fetchImportDetails = () => {
    const payload = _.cloneDeep(formData);
    const url = `users/${userData?.uuid}/imports/${selectedItem?.uuid}/details/search`;
    axiosInstance
      .post(url, payload)
      .then((response) => {
        const data = response?.data?.data || [];
        const filtered = response?.data?.recordsFiltered || 0;
        setTableData(data);
        setRecordsFiltered(filtered);

        if (filtered < 15 && pageCount !== 10) {
          setPageCount(10);
        } else if (filtered >= 15 && filtered < 20 && pageCount !== 15) {
          setPageCount(15);
        } else if (filtered >= 20 && pageCount !== 15) {
          setPageCount(15);
        }
      })
      .catch(() => {
        toast.error(t("There was an error fetching backlinks offers"));
      });
  };

  useEffect(() => {
    const payload = { ...formData };
    const order = [];
    Object.keys(columnSorts).forEach((columnIndex) => {
      if (columnSorts[columnIndex]) {
        order.push({
          column: columnIndex,
          dir: columnSorts[columnIndex],
        });
      }
    });
    if (order.length) {
      payload.order = order;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSorts]);

  const previousPayloadRef = useRef(null);
  useEffect(() => {
    const payload = { ...formData };
    if (!_.isEqual(previousPayloadRef.current, payload)) {
      previousPayloadRef.current = payload;
      fetchImportDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const updateFilter = (key, value) => {
    setFormData((prevData) => {
      const updatedFilters =
        value === null
          ? prevData.search.filters.filter((filter) => filter.key !== key)
          : [
              ...prevData.search.filters.filter((filter) => filter.key !== key),
              { key: key, value: value },
            ];

      return {
        ...prevData,
        search: {
          ...prevData.search,
          filters: updatedFilters,
        },
      };
    });
  };

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      search: {
        ...prevData.search,
        value: searchTerm,
      },
    }));
  }, [searchTerm]);

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    _.debounce((searchTerm) => {
      setActivePage(1);
      setSearchTerm(searchTerm);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(search);
    return () => {
      debouncedSearch.cancel();
    };
  }, [search, debouncedSearch]);

  const hsInitRef = useRef(null);
  useEffect(() => {
    if (!hsInitRef.current) {
      HSCore.components.HSTomSelect.init(".js-select-search");
      // eslint-disable-next-line react-hooks/exhaustive-deps
      hsInitRef.current = true;
    }
  }, []);

  return (
    <main id="content" role="main" className="main">
      <div className="content container-fluid">
        <div className="page-header" style={{ paddingBottom: "1em" }}>
          <div className="row align-items-center mb-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb breadcrumb-no-gutter">
                <li className="breadcrumb-item">
                  {/* eslint-disable-next-line */}
                  <a href="javascript:;"
                    className="breadcrumb-link"
                    style={{ opacity: "0.6" }}
                  >
                    {t("Websites")}
                  </a>
                </li>
                <li className="breadcrumb-item">
                  <a className="breadcrumb-link" href="/offers/import">
                    {t("Your Imports")}
                  </a>
                </li>
              </ol>
            </nav>
            <div className="col-md d-md-flex gap-4 align-items-center mb-sm-2 mb-2 mb-md-0">
              <h1
                className="page-header-title"
                style={{ margin: "0", fontSize: "28px" }}
              >
                {t("File")}: {selectedItem?.file_name}
              </h1>
              <span
                className="badge bg-soft-success text-success py-2 fs-6 mt-3 mt-md-0"
                style={{ width: "95px" }}
              >
                {importStatus[selectedItem?.status]?.title}
              </span>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header justify-content-sm-between d-flex flex-row gap-2">
            <div className="d-flex flex-wrap gap-3 w-100">
              <div className="search-input">
                <div
                  className="input-group input-group-merge input-group-flush border rounded-2"
                  style={{ height: "47px" }}
                >
                  <div className="input-group-prepend input-group-text px-2">
                    <i className="bi-search"></i>
                  </div>
                  <input
                    id="datatableSearch"
                    type="search"
                    className="form-control custom-placeholder"
                    placeholder={t("Search Imports")}
                    aria-label="Search Import"
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-xxl-flex flex-wrap justify-content-between d-none gap-3">
                <ul
                  className="nav nav-segment flex-wrap gap-2 d-none d-xxxl-flex justify-content-between"
                  style={{ width: "293px", height: "48px" }}
                  role="tablist"
                >
                  {allParsing.map((parsing) => {
                    return (
                      <li className="nav-item" key={parsing.value}>
                        <a
                          className={`js-loading-state-invoker nav-link ${
                            activeParsTab === parsing.value ? "active" : ""
                          }`}
                          id="nav-one-eg1-tab"
                          data-bs-toggle="pill"
                          href="#nav-one-eg1"
                          role="tab"
                          style={{ fontSize: "16px", paddingBottom: "0.5rem" }}
                          aria-controls="nav-one-eg1"
                          aria-selected={activeParsTab === parsing.value}
                          onClick={(e) => {
                            e.preventDefault();
                            handleParsingTabClick(parsing.value);
                          }}
                          data-hs-loading-state-options='{
                        "targetEl": "#nav-one-eg1",
                        "removeLoaderDelay": "300"
                      }'
                        >
                          {parsing.name}
                        </a>
                      </li>
                    );
                  })}
                </ul>
                <ul
                  className="nav nav-segment justify-content-between"
                  style={{ width: "434px", height: "48px" }}
                  role="tablist"
                >
                  {allProcessing.map((proce) => {
                    return (
                      <li className="nav-item" key={proce.value}>
                        <a
                          className={`js-loading-state-invoker nav-link ${
                            activeProcessTab === proce.value ? "active" : ""
                          }`}
                          id="nav-one-eg1-tab"
                          data-bs-toggle="pill"
                          href="#nav-one-eg1"
                          role="tab"
                          style={{ fontSize: "16px", paddingBottom: "0.5rem" }}
                          aria-controls="nav-one-eg1"
                          aria-selected={activeProcessTab === proce.value}
                          onClick={(e) => {
                            e.preventDefault();
                            handleProcessingTabClick(proce.value);
                          }}
                          data-hs-loading-state-options='{
                        "targetEl": "#nav-one-eg1",
                        "removeLoaderDelay": "300"
                      }'
                        >
                          {proce.name}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            <div className="d-grid d-flex justify-content-end align-items-center gap-2">
              <div className="btn-group d-xxxl-none d-block">
                <button
                  className="btn btn-white text-nowrap"
                  type="button"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvasImportFilter"
                  aria-controls="offcanvasImportFilter"
                >
                  <FilterIcon />{" "}
                  <span className="ps-1 align-middle">{t("Filters")}</span>{" "}
                  {formData?.search?.filters.length
                    ? `(${formData?.search?.filters.length})`
                    : ""}
                </button>
              </div>
              <div className="dropdown">
                <button
                  type="button"
                  className="btn btn-white w-100 text-nowrap text-secondary"
                  id="showHideDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-auto-close="outside"
                >
                  <i className="bi bi-layout-three-columns me-1 text-secondary"></i>{" "}
                  {t("Columns")}
                </button>

                <div
                  className="dropdown-menu dropdown-menu-end dropdown-card"
                  aria-labelledby="showHideDropdown"
                  style={{ width: "15rem" }}
                >
                  <div className="card card-sm">
                    <div className="card-body">
                      <div className="d-grid gap-3">
                        {Object.keys(columns).map((key) => (
                          <label
                            key={key}
                            className="row form-check form-switch"
                            htmlFor={`toggleColumn_${key}`}
                          >
                            <span className="col-8 col-sm-9 ms-0">
                              <span className="me-2">
                                {t(_.startCase(key))}
                              </span>
                            </span>
                            <span className="col-4 col-sm-3 text-end">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`toggleColumn_${key}`}
                                checked={columns[key]}
                                onChange={() => handleToggle(key)}
                              />
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ImportDetailsTable
            data={tableData}
            setData={setTableData}
            columnsVisibility={columns}
            setColumnSorts={setColumnSorts}
            columnSorts={columnSorts}
          />
          <TablePagination
            activePage={activePage}
            setActivePage={setActivePage}
            recordsFiltered={recordsFiltered}
            setPageCount={setPageCount}
            pageCount={pageCount}
          />
        </div>
      </div>
      <Footer />
      <ImportFilterModal
        allParsing={allParsing}
        allProcessing={allProcessing}
        setActiveParsTab={setActiveParsTab}
        activeParsTab={activeParsTab}
        setActiveProcessTab={setActiveProcessTab}
        activeProcessTab={activeProcessTab}
        updatePendingFilter={updatePendingFilter}
        pendingFilters={pendingFilters}
        setFormData={setFormData}
      />
    </main>
  );
};

export default ImportDetails;
